import React, { useContext, useState } from "react";
import { X, CalendarClock } from "lucide-react";
import { createPortal } from "react-dom";
import { ThemeContext } from "../../../../constant/theme";
import { LanguageContext } from "../../../../constant/LanguageContext";
import text from "../../constants/reportsTranslations";

const ScheduleReportModal = ({ open, onClose, reportType, onCreate }) => {
  const { theme } = useContext(ThemeContext);
  const { language } = useContext(LanguageContext);
  const isDark = theme === "dark";
  const isRTL = language === "ar";

  const [frequency, setFrequency] = useState("WEEKLY");
  const [format, setFormat] = useState("pdf");
  const [dayOfWeek, setDayOfWeek] = useState(1);
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [recipientsText, setRecipientsText] = useState("");
  const [runImmediately, setRunImmediately] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  if (!open) return null;

  const dayNames = language === "ar"
    ? ["الأحد", "الإثنين", "الثلاثاء", "الأربعاء", "الخميس", "الجمعة", "السبت"]
    : ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

  const handleSubmit = async () => {
    const recipients = recipientsText.split(",").map((r) => r.trim()).filter(Boolean);
    if (recipients.length === 0) { setError(text[language].scheduleRecipients); return; }
    setSubmitting(true);
    setError(null);
    try {
      await onCreate({ reportType, format, frequency, recipients, dayOfWeek, dayOfMonth, runImmediately });
      onClose();
    } catch (err) {
      setError(err.message || text[language].scheduleError);
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, zIndex: 99991, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, backdropFilter: "blur(6px)" }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(440px, 100%)", borderRadius: 20, overflow: "hidden",
          background: isDark ? "rgba(15,23,42,0.98)" : "#fff", color: isDark ? "#f8fafc" : "#101827",
          border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)"}`,
          boxShadow: isDark ? "0 24px 60px rgba(0,0,0,0.55)" : "0 24px 60px rgba(0,0,0,0.18)",
        }}
      >
        <div style={{ padding: 20, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`, flexDirection: isRTL ? "row-reverse" : "row" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexDirection: isRTL ? "row-reverse" : "row" }}>
            <CalendarClock size={18} color="#8b5cf6" />
            <span style={{ fontSize: 16, fontWeight: 700 }}>{text[language].scheduleTitle}</span>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" style={{ background: "none", border: "none", cursor: "pointer", color: "inherit", opacity: 0.6 }}><X size={20} /></button>
        </div>

        <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <label style={fieldLabel(isDark)}>{text[language].scheduleFrequency}</label>
            <div style={{ display: "flex", gap: 6 }}>
              {["DAILY", "WEEKLY", "MONTHLY"].map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setFrequency(f)}
                  style={{
                    flex: 1, padding: "8px 0", borderRadius: 10, border: "none", cursor: "pointer", fontSize: 12.5, fontWeight: 700,
                    background: frequency === f ? "linear-gradient(135deg, #8b5cf6, #6d28d9)" : (isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)"),
                    color: frequency === f ? "#fff" : (isDark ? "#e2e8f0" : "#334155"),
                  }}
                >
                  {text[language][`schedule${f.charAt(0)}${f.slice(1).toLowerCase()}`]}
                </button>
              ))}
            </div>
          </div>

          {frequency === "WEEKLY" && (
            <div>
              <label style={fieldLabel(isDark)}>{text[language].scheduleDayOfWeek}</label>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(64px, 1fr))", gap: 6 }}>
                {dayNames.map((d, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setDayOfWeek(i)}
                    style={{
                      padding: "8px 4px", borderRadius: 10, border: "none", cursor: "pointer", fontSize: 12, fontWeight: 700,
                      background: dayOfWeek === i ? "linear-gradient(135deg, #8b5cf6, #6d28d9)" : (isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)"),
                      color: dayOfWeek === i ? "#fff" : (isDark ? "#e2e8f0" : "#334155"),
                    }}
                  >
                    {d}
                  </button>
                ))}
              </div>
            </div>
          )}
          {frequency === "MONTHLY" && (
            <div>
              <label style={fieldLabel(isDark)}>{text[language].scheduleDayOfMonth}</label>
              <input type="number" min={1} max={28} value={dayOfMonth} onChange={(e) => setDayOfMonth(Number(e.target.value))} style={fieldInput(isDark)} />
            </div>
          )}

          <div>
            <label style={fieldLabel(isDark)}>{text[language].scheduleFormat}</label>
            <div style={{ display: "flex", gap: 6 }}>
              {["pdf", "xlsx"].map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setFormat(f)}
                  style={{
                    flex: 1, padding: "8px 0", borderRadius: 10, border: "none", cursor: "pointer", fontSize: 12.5, fontWeight: 700,
                    background: format === f ? "#3b82f6" : (isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)"),
                    color: format === f ? "#fff" : (isDark ? "#e2e8f0" : "#334155"),
                  }}
                >
                  {f.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label style={fieldLabel(isDark)}>{text[language].scheduleRecipients}</label>
            <input
              value={recipientsText}
              onChange={(e) => setRecipientsText(e.target.value)}
              placeholder="a@company.com, b@company.com"
              style={fieldInput(isDark)}
            />
          </div>

          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, cursor: "pointer", flexDirection: isRTL ? "row-reverse" : "row" }}>
            <input type="checkbox" checked={runImmediately} onChange={(e) => setRunImmediately(e.target.checked)} />
            {text[language].scheduleRunImmediately}
          </label>

          {error && <div style={{ fontSize: 12, color: "#ef4444" }}>{error}</div>}

          <div style={{ display: "flex", gap: 8, marginTop: 4, flexDirection: isRTL ? "row-reverse" : "row" }}>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting}
              style={{ flex: 1, padding: "10px 0", borderRadius: 10, border: "none", cursor: submitting ? "not-allowed" : "pointer", background: "linear-gradient(135deg, #8b5cf6, #6d28d9)", color: "#fff", fontSize: 13, fontWeight: 700, opacity: submitting ? 0.7 : 1 }}
            >
              {text[language].scheduleCreate}
            </button>
            <button
              type="button"
              onClick={onClose}
              style={{ flex: 1, padding: "10px 0", borderRadius: 10, border: `1px solid ${isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.1)"}`, background: "transparent", color: isDark ? "#e2e8f0" : "#334155", fontSize: 13, fontWeight: 700, cursor: "pointer" }}
            >
              {text[language].scheduleCancel}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

const fieldLabel = (isDark) => ({ display: "block", fontSize: 11.5, fontWeight: 700, marginBottom: 6, color: isDark ? "rgba(255,255,255,0.55)" : "rgba(30,41,59,0.55)", textTransform: "uppercase" });
const fieldInput = (isDark) => ({ width: "100%", padding: "9px 12px", borderRadius: 10, border: `1px solid ${isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.1)"}`, background: isDark ? "rgba(255,255,255,0.05)" : "#fff", color: isDark ? "#f1f5f9" : "#1e293b", fontSize: 13, outline: "none", boxSizing: "border-box" });

export default ScheduleReportModal;
